
import { useState, useEffect } from "react";
import { InventoryItem } from "@/types/inventory";

const SUGGESTION_STORAGE_KEY = "inventory_search_suggestions";
const MAX_SUGGESTIONS = 20;

export function useProductSuggestions() {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  
  // Load saved suggestions on component mount
  useEffect(() => {
    const savedSuggestions = localStorage.getItem(SUGGESTION_STORAGE_KEY);
    if (savedSuggestions) {
      setSuggestions(JSON.parse(savedSuggestions));
    }
  }, []);
  
  // Add a new suggestion to the list
  const addSuggestion = (productName: string) => {
    if (!productName || productName.length < 3) return;
    
    setSuggestions(prevSuggestions => {
      // Remove existing occurrence of this product name
      const filteredSuggestions = prevSuggestions.filter(
        item => item.toLowerCase() !== productName.toLowerCase()
      );
      
      // Add the new suggestion at the beginning
      const newSuggestions = [productName, ...filteredSuggestions].slice(0, MAX_SUGGESTIONS);
      
      // Save to local storage
      localStorage.setItem(SUGGESTION_STORAGE_KEY, JSON.stringify(newSuggestions));
      
      return newSuggestions;
    });
  };
  
  // Add multiple product suggestions at once
  const addProductSuggestions = (products: InventoryItem[]) => {
    if (!products || products.length === 0) return;
    
    const newSuggestions = products
      .filter(product => product.product_naam && product.product_naam.length >= 3)
      .map(product => product.product_naam);
      
    if (newSuggestions.length === 0) return;
    
    setSuggestions(prevSuggestions => {
      const combined = [...new Set([...newSuggestions, ...prevSuggestions])].slice(0, MAX_SUGGESTIONS);
      localStorage.setItem(SUGGESTION_STORAGE_KEY, JSON.stringify(combined));
      return combined;
    });
  };
  
  // Clear all suggestions
  const clearSuggestions = () => {
    localStorage.removeItem(SUGGESTION_STORAGE_KEY);
    setSuggestions([]);
  };
  
  return { suggestions, addSuggestion, addProductSuggestions, clearSuggestions };
}
